import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Tooltip,
  Grid,
  Paper,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import { API_URL } from "../config";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ListAltIcon from "@mui/icons-material/ListAlt";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A", light: "#FEF3C7", dark: "#92680C" },
    error: { main: "#DC2626", light: "#FEE2E2" },
    success: { main: "#059669", light: "#D1FAE5" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 1060, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "11px 28px",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 14px rgba(37,99,235,0.28)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,0.38)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const StyledInput = styled("input")(() => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px",
  outline: "none",
  boxSizing: "border-box",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: "#9CA3AF",
  marginBottom: 6,
  fontFamily: "'Sora', sans-serif",
}));

const YearCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "itemdelay",
})(({ theme, itemdelay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha("#000", 0.07)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
  padding: "20px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  animation: `${cardEntrance} 0.45s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.22s ease",
  "&:hover": {
    boxShadow: "0 6px 20px rgba(37,99,235,0.09), 0 10px 32px rgba(0,0,0,0.07)",
    transform: "translateY(-3px)",
    borderColor: alpha("#2563EB", 0.22),
    "& .card-arrow": { transform: "translateX(3px)", opacity: 1 },
    "& .year-badge": {
      boxShadow: "0 6px 18px rgba(37,99,235,0.38)",
      transform: "scale(1.06)",
    },
  },
}));

const YearBadge = styled(Box)(() => ({
  width: 56,
  height: 56,
  borderRadius: 14,
  flexShrink: 0,
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 12px rgba(37,99,235,0.28)",
  marginBottom: 14,
  transition: "all 0.22s ease",
}));

const ActionBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "btncolor",
})(({ theme, btncolor }) => {
  const map = {
    edit: { bg: alpha("#C4922A", 0.1), color: "#C4922A", hbg: "#C4922A" },
    delete: { bg: alpha("#DC2626", 0.08), color: "#DC2626", hbg: "#DC2626" },
  };
  const s = map[btncolor] || map.edit;
  return {
    minWidth: "unset",
    padding: "5px 12px",
    borderRadius: 7,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "none",
    background: s.bg,
    color: s.color,
    fontFamily: "'Sora', sans-serif",
    transition: "all 0.18s ease",
    "&:hover": {
      background: s.hbg,
      color: "#fff",
      transform: "translateY(-1px)",
      boxShadow: `0 4px 10px ${alpha(s.hbg, 0.3)}`,
    },
  };
});

const SearchBox = styled(Box)(({ theme }) => ({
  position: "relative",
  "& input": {
    paddingLeft: 40,
  },
  "& .search-icon": {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 16,
    color: "#9CA3AF",
    pointerEvents: "none",
  },
}));

const SkeletonCard = styled(Box)(() => ({
  borderRadius: 16,
  border: `1px solid ${alpha("#000", 0.06)}`,
  padding: "20px",
  background: "#fff",
  animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
}));

const SkeletonBlock = ({ w = "100%", h = 14, mb = 8, radius = 6 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: radius,
      background: alpha("#000", 0.07),
      mb: `${mb}px`,
    }}
  />
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminTheoryYearPage() {
  const navigate = useNavigate();
  const { theorySubjectId } = useParams();

  const [theoryYears, setTheoryYears] = useState([]);
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredTheoryYears, setFilteredTheoryYears] = useState([]);
  const [editingYearId, setEditingYearId] = useState(null);

  const fetchTheoryYears = useCallback(() => {
    setFetching(true);
    fetch(`${API_URL}/admin/theoryYears/${theorySubjectId}/`)
      .then((res) => res.json())
      .then((data) => {
        setTheoryYears(data);
        setFilteredTheoryYears(data);
        setFetching(false);
      })
      .catch(() => {
        alert("Failed to fetch years.");
        setFetching(false);
      });
  }, [theorySubjectId]);

  useEffect(() => {
    fetchTheoryYears();
  }, [fetchTheoryYears]);

  const resetForm = () => {
    setYear("");
    setEditingYearId(null);
  };

  const submitYear = () => {
    if (!year.trim()) {
      alert("Year is required.");
      return;
    }
    setLoading(true);
    const method = editingYearId ? "PUT" : "POST";
    const url = editingYearId
      ? `${API_URL}/admin/theoryYear/${editingYearId}/`
      : `${API_URL}/admin/theoryYears/${theorySubjectId}/`;

    fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ year }),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data) => {
        if (editingYearId) {
          const updated = theoryYears.map((i) =>
            i.id === editingYearId ? data : i
          );
          setTheoryYears(updated);
          setFilteredTheoryYears(
            updated.filter((i) => i.year.toString().includes(searchQuery))
          );
        } else {
          setTheoryYears([data, ...theoryYears]);
          setFilteredTheoryYears([data, ...filteredTheoryYears]);
        }
        resetForm();
      })
      .catch(() => alert(`Failed to ${editingYearId ? "update" : "add"} year.`))
      .finally(() => setLoading(false));
  };

  const editYear = (id) => {
    const selected = theoryYears.find((y) => y.id === id);
    if (selected) {
      setYear(selected.year.toString());
      setEditingYearId(id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const deleteYear = (id) => {
    if (window.confirm("Are you sure you want to delete this year?")) {
      fetch(`${API_URL}/admin/theoryYear/${id}/`, { method: "DELETE" })
        .then(() => {
          const updated = theoryYears.filter((y) => y.id !== id);
          setTheoryYears(updated);
          setFilteredTheoryYears(
            updated.filter((i) => i.year.toString().includes(searchQuery))
          );
          if (editingYearId === id) resetForm();
        })
        .catch(() => alert("Failed to delete year."));
    }
  };

  const navigateToNumbers = (theoryYearId) => navigate(`/admin-theory-question/${theoryYearId}`);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setFilteredTheoryYears(
      query === ""
        ? theoryYears
        : theoryYears.filter((i) => i.year.toString().includes(query))
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
              animation: `${fadeUp} 0.4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 27 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                {editingYearId ? "Edit Theory Year" : "Theory Years"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Subject ID:{" "}
                <strong style={{ color: "#4B5563" }}>{theorySubjectId}</strong>
              </Typography>
            </Box>
            <Chip
              label={editingYearId ? "Editing" : "New"}
              size="small"
              sx={{
                ml: "auto",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                background: editingYearId
                  ? alpha("#C4922A", 0.12)
                  : alpha("#2563EB", 0.1),
                color: editingYearId ? "#C4922A" : "#2563EB",
                border: `1px solid ${
                  editingYearId ? alpha("#C4922A", 0.25) : alpha("#2563EB", 0.2)
                }`,
              }}
            />
          </Box>

          {/* ── Form Card ── */}
          <SectionCard delay={50}>
            <CardLabel>
              {editingYearId ? "Edit Year" : "Add New Year"}
            </CardLabel>

            <Box sx={{ mb: 2.5, maxWidth: 240 }}>
              <FieldLabel>Theory Year *</FieldLabel>
              <Box sx={{ position: "relative" }}>
                <CalendarMonthIcon
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: 15,
                    color: "text.disabled",
                    pointerEvents: "none",
                  }}
                />
                <StyledInput
                  type="number"
                  placeholder="e.g. 2024"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  style={{ paddingLeft: 36 }}
                />
              </Box>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <PrimaryBtn
                startIcon={
                  editingYearId ? (
                    <CheckIcon sx={{ fontSize: "16px !important" }} />
                  ) : (
                    <AddIcon sx={{ fontSize: "16px !important" }} />
                  )
                }
                disabled={loading || !year.trim()}
                onClick={submitYear}
              >
                {loading
                  ? editingYearId
                    ? "Updating…"
                    : "Adding…"
                  : editingYearId
                  ? "Update Year"
                  : "Add Year"}
              </PrimaryBtn>
              {editingYearId && (
                <Button
                  onClick={resetForm}
                  sx={{
                    borderRadius: 2.5,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${alpha("#000", 0.12)}`,
                    color: "text.secondary",
                    px: 2.5,
                    fontFamily: "'Sora', sans-serif",
                    "&:hover": { background: alpha("#000", 0.04) },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </SectionCard>

          {/* ── Years List Card ── */}
          <SectionCard delay={120}>
            {/* Header row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <CardLabel sx={{ mb: 0 }}>Theory Years</CardLabel>
              {!fetching && (
                <Chip
                  label={`${filteredTheoryYears.length} of ${
                    theoryYears.length
                  } year${theoryYears.length !== 1 ? "s" : ""}`}
                  size="small"
                  sx={{
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 700,
                    fontSize: 11,
                    background: alpha("#2563EB", 0.08),
                    color: "#2563EB",
                    border: `1px solid ${alpha("#2563EB", 0.18)}`,
                  }}
                />
              )}
            </Box>

            {/* Search */}
            <SearchBox sx={{ mb: 3 }}>
              <SearchIcon className="search-icon" />
              <StyledInput
                placeholder="Search by year…"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </SearchBox>

            {/* ── Skeleton loading ── */}
            {fetching ? (
              <Grid container spacing={2.5}>
                {[...Array(6)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <SkeletonCard sx={{ animationDelay: `${i * 80}ms` }}>
                      <SkeletonBlock w={56} h={56} mb={14} radius={14} />
                      <SkeletonBlock w="55%" h={20} mb={8} />
                      <SkeletonBlock w="35%" h={12} mb={20} />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <SkeletonBlock w="50%" h={30} mb={0} radius={7} />
                        <SkeletonBlock w="50%" h={30} mb={0} radius={7} />
                      </Box>
                    </SkeletonCard>
                  </Grid>
                ))}
              </Grid>
            ) : filteredTheoryYears.length === 0 ? (
              /* ── Empty state ── */
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    background: alpha("#2563EB", 0.07),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <CalendarMonthIcon
                    sx={{ fontSize: 26, color: "primary.main", opacity: 0.5 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 600,
                  }}
                >
                  {searchQuery
                    ? `No years match "${searchQuery}"`
                    : "No theory years yet"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.4,
                  }}
                >
                  {searchQuery
                    ? "Try a different search term"
                    : "Use the form above to add your first year"}
                </Typography>
              </Box>
            ) : (
              /* ── Year cards ── */
              <Grid container spacing={2.5}>
                {filteredTheoryYears.map((item, i) => (
                  <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <YearCard
                      itemdelay={i * 55}
                      onClick={() => navigateToNumbers(item.id)}
                    >
                      {/* Year badge */}
                      <YearBadge className="year-badge">
                        <Typography
                          sx={{
                            fontSize: 17,
                            fontWeight: 800,
                            color: "#fff",
                            fontFamily: "'DM Serif Display', serif",
                            letterSpacing: "-0.5px",
                          }}
                        >
                          {item.year}
                        </Typography>
                      </YearBadge>

                      {/* Label */}
                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: "text.primary",
                          fontFamily: "'DM Serif Display', serif",
                          mb: 0.4,
                          lineHeight: 1.2,
                        }}
                      >
                        Year {item.year}
                      </Typography>

                      <Box sx={{ flex: 1 }} />

                      {/* Navigate hint */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mb: 1.5,
                          mt: 1,
                        }}
                      >
                        <ListAltIcon
                          sx={{ fontSize: 12, color: "text.disabled" }}
                        />
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "text.disabled",
                            fontFamily: "'Sora', sans-serif",
                          }}
                        >
                          Click to manage numbers
                        </Typography>
                        <ArrowForwardIosIcon
                          className="card-arrow"
                          sx={{
                            fontSize: 10,
                            color: "primary.main",
                            ml: "auto",
                            opacity: 0.5,
                            transition: "all 0.2s ease",
                          }}
                        />
                      </Box>

                      {/* Actions */}
                      <Stack
                        direction="row"
                        spacing={0.8}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title="Edit year" arrow>
                          <ActionBtn
                            btncolor="edit"
                            sx={{ flex: 1 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              editYear(item.id);
                            }}
                          >
                            <EditIcon sx={{ fontSize: 13, mr: 0.4 }} />
                            Edit
                          </ActionBtn>
                        </Tooltip>
                        <Tooltip title="Delete year" arrow>
                          <ActionBtn
                            btncolor="delete"
                            sx={{ flex: 1 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteYear(item.id);
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 13, mr: 0.4 }} />
                            Delete
                          </ActionBtn>
                        </Tooltip>
                      </Stack>
                    </YearCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
}
